import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, Loader2, ArrowLeft, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "/admixio-logo.png";

const LoginPage = () => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [mockedOtp, setMockedOtp] = useState('');
  
  const { sendOTP, verifyOTP, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'super_admin') navigate('/admin');
      else if (user.role === 'agent') navigate('/agent');
      else if (user.role === 'institution') navigate('/institution');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await sendOTP(email);
      setMockedOtp(response.mocked_otp || '');
      setStep('otp');
      toast.success('OTP sent to your email!');
    } catch (error) {
      // Still allow proceeding to OTP screen — master OTP works without send-otp
      setStep('otp');
      toast.info('Use Master OTP to login');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOTP(email, otp);
      
      if (result.requiresRegistration) {
        navigate('/register', { state: { email } });
      } else {
        // Check if user is approved
        if (result.user && !result.user.is_active) {
          toast.error('Your account is pending admin approval. Please wait for activation.');
          return;
        }
        toast.success('Login successful!');
        // Role-based navigation handled by useEffect above
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid OTP';
      if (error.response?.status === 403) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0B1E] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full bg-[#00D4FF]/10 blur-[120px] animate-breathe" />
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-[#8B5CF6]/10 blur-[100px] animate-breathe" style={{ animationDelay: '2s' }} />
      </div>

      {/* Particles */}
      <div className="particles">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={`particle-${i}`}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              bottom: '-10px',
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      {/* Login Card */}
      <div className="relative w-full max-w-md z-10" data-testid="login-container">
        <div className="glass-strong border border-[#2D2654] shadow-[0_8px_32px_rgba(0,212,255,0.15)] rounded-2xl p-8">

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Link to="/">
              <img 
                src={LOGO_URL}
                alt="Admixio" 
                className="h-16 animate-logo-glow"
                data-testid="login-logo"
              />
            </Link>
          </div>

          {step === 'email' ? (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Welcome to Admixio
                </h1>
                <p className="text-[#B8B4CE] text-sm">
                  Enter your email to access your portal
                </p>
              </div>

              <form onSubmit={handleSendOTP} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm text-[#B8B4CE]">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B678A]" strokeWidth={1.5} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-10 bg-[#0D0B1E] border-[#2D2654] text-white placeholder:text-[#6B678A] focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF]"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-gradient"
                  data-testid="send-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Enter OTP
                </h1>
                <p className="text-[#B8B4CE] text-sm">
                  We sent a 6-digit code to <span className="text-[#00D4FF]">{email}</span>
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP
                    value={otp}
                    onChange={setOtp}
                    maxLength={6}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup className="gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot
                          key={`otp-slot-${index}`}
                          index={index}
                          className="w-12 h-14 text-xl bg-[#0D0B1E] border-[#2D2654] text-white focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] rounded-lg"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length !== 6}
                  className="w-full btn-gradient"
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Verify & Continue
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setOtp('');
                  }}
                  className="w-full text-[#B8B4CE] text-sm hover:text-white transition-colors flex items-center justify-center gap-2"
                  data-testid="back-to-email-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Use different email
                </button>
              </div>
            </>
          )}

          <div className="mt-8 pt-6 border-t border-[#2D2654]">
            <p className="text-center text-sm text-[#6B678A]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#00D4FF] hover:underline" data-testid="signup-link">
                Register now
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-[#6B678A] hover:text-[#B8B4CE] transition-colors inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
